import { AstradaBinData } from 'src/context/AstradaContext/AstradaContext'

type ResolveEnrollmentEligibilityParams = {
  /** BIN-derived eligibility captured when the card number was entered. */
  binData: AstradaBinData
  /**
   * `/features` → `PHYSICAL_CARD_TRANSACTIONS_FEED`. Only meaningful for a
   * SaaS-eligible issuer, where the selected plan is what grants the feed.
   */
  physicalCardTransactionsFeedEnabled: boolean
  /**
   * `saasEnabled` from the issuer's SaaS-eligibility response. `undefined`
   * while it is unresolved (loading, or no issuer id to look up).
   */
  saasEnabled?: boolean
}

/**
 * Decides the eligibility handed to `<ReviewEnrollCard>` — which drives both the
 * acknowledgements shown and the enrollment endpoint called (`/physical` vs
 * `/virtual`).
 *
 * For a SaaS-eligible issuer the user passes through pricing, and the plan they
 * pick is what grants `PHYSICAL_CARD_TRANSACTIONS_FEED`, so a disabled feature
 * means physical enrollment would be rejected — downgrade to `VIRTUAL_ONLY`.
 *
 * When the issuer is **not** SaaS-enabled the user skips pricing entirely and no
 * plan ever grants that feature, so the `/features` value doesn't describe this
 * user's real entitlements and must not gate enrollment. The rule there is
 * simply: never downgrade. The BIN response already decided physical
 * eligibility — `PHYSICAL_AND_VIRTUAL` is only produced from a truthy
 * `astradaEnabled` (`calculateAstradaEligibility`), and an astrada-disabled BIN
 * arrives as `VIRTUAL_ONLY` / `INELIGIBLE` and returns untouched below. Note
 * that this means no further client-side check stands between the user and the
 * `/physical` call on that path.
 */
export function resolveEnrollmentEligibility(
  params: ResolveEnrollmentEligibilityParams,
): AstradaBinData['eligibility'] {
  const { binData, physicalCardTransactionsFeedEnabled, saasEnabled } = params

  if (binData.eligibility !== 'PHYSICAL_AND_VIRTUAL') {
    return binData.eligibility
  }

  if (saasEnabled === false) {
    return 'PHYSICAL_AND_VIRTUAL'
  }

  return physicalCardTransactionsFeedEnabled
    ? 'PHYSICAL_AND_VIRTUAL'
    : 'VIRTUAL_ONLY'
}

/**
 * `true` when the render must block on the `/features` lookup before handing an
 * eligibility to `<ReviewEnrollCard>`. Only `PHYSICAL_AND_VIRTUAL` cards can be
 * downgraded, and only when SaaS eligibility hasn't ruled the feature out of the
 * decision.
 */
export function needsPhysicalCardTransactionsFeedFeature(params: {
  binData?: AstradaBinData
  saasEnabled?: boolean
}): boolean {
  const { binData, saasEnabled } = params

  return (
    binData?.eligibility === 'PHYSICAL_AND_VIRTUAL' && saasEnabled !== false
  )
}
